import { useState, type MutableRefObject, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Box, CircularProgress, Link, Paper } from '@mui/material'

import { PillSelect } from '@/apps/latest/components/molecules/PillSelect'
import {
  MemoPanel,
  type MemoFlush,
} from '@/apps/latest/features/yakureki/components/MemoPanel'
import { colorTokens } from '@/apps/latest/shared/styles/colorTokens'
import { BodyText } from '@/components/atoms/BodyText'
import { getYakurekiDetail } from '@/shared/apis/yakureki'
import { queryKeys } from '@/shared/hooks/queries/queryKeys'
import { RADIUS } from '@/shared/styles/radii'
import type { YakurekiDetail } from '@/shared/types/yakurekiCore'

import {
  getNutritionReport,
  type NutritionReport,
  type NutritionRound,
} from './api'
import { formatYmd } from './format'
import { NutritionExportButton } from './NutritionExportButton'
import { NutritionRoundMenu } from './NutritionRoundMenu'
import { NutritionRoundTabs, type RoundTab } from './NutritionRoundTabs'
import { nutritionReportKey } from './useNutritionReport'
import {
  useNutritionCustomer,
  useNutritionRounds,
} from './useNutritionCustomers'

// 今回 (カルテが録音とともに持つ記録) の中身。着地していなければタブは出さず、回の行だけ (録る・資料・メモは上の段)
interface CurrentRound {
  event: YakurekiDetail
  report: NutritionReport
  // 今回の報告書が着地したか (page の段階が完了)
  landed: boolean
  // 作り直す前にメモの保存を待ち合わせる口 (page が持つ)
  memoFlushRef: MutableRefObject<MemoFlush | null>
}

interface Props {
  customerId: string
  // 今回。カルテで今回がまだ始まっていなければ無い (前の回だけ)
  current?: CurrentRound
  // 今回を録っている間・送っている間・作成中。どの回にも記録を削除の ⋯ を出さない
  busy?: boolean
  // 回が消えた後に呼ぶ (消えたのが今回なら、カルテは今回の無い形に戻る)
  onDeleted?: (eventId: string) => void
}

// 回の選択が読む欄 (回の一覧の行と同じ形。今回は page が持つ記録から作る)
type Round = Pick<
  NutritionRound,
  'id' | 'recorded_on' | 'session_number' | 'landed'
>

// カルテの「回」の層 (design-book ADR-0082・ADR-0086)。時間軸 (お客様の回) をタブより 1 つ上に置き、選んだ回を
// 報告書 / 栄養価 / 資料 / 会話 のタブで出す。どの回も同じ見た目でその場で直せる (録る・録り直す・作る・作り直すは
// 名前の行の今回の手)。回目を数えるのはこの選択 1 か所。字に「今回」「前回」を添え、どの回を見ているかを言う。
// 回の行は記録が 1 つでもあれば出し、右端に選んだ回の 出す (着地していれば) と ⋯ (記録を削除) を置く。
// 初めは、今回が着地していれば今回、着地する前 (録る前〜作成中。相談の最中に前の回を見る) は前回。
// 回は数が増える (月 1 回で 1 年 12 回) ので、横に並べず 1 つの選択 (PillSelect) で持つ。今回を選ぶ規則は back が持つ
// (回の一覧の current)。
export const NutritionRecordRounds = ({
  customerId,
  current,
  busy = false,
  onDeleted,
}: Props) => {
  const { data } = useNutritionRounds(customerId)
  const { data: customer } = useNutritionCustomer(customerId)
  const others: Round[] = (data ?? []).filter((r) => r.id !== current?.event.id)
  const now: Round | null = current
    ? {
        id: current.event.id,
        recorded_on: current.report.stamp.recorded_on,
        session_number: current.report.fields.session_number ?? '',
        landed: current.landed,
      }
    : null
  const rounds = now ? [now, ...others] : others
  const previousId = others.find((r) => r.landed)?.id

  const [chosen, setChosen] = useState<string | null>(null)
  // 開いているタブは回の外で持つ (回を替えても同じタブのまま)
  const [tab, setTab] = useState<RoundTab>('report')
  // 着地する前の初めは「前回」の字の付く回 (着地しないまま置いた前の回が間にあっても、それを前回と見ない)
  const initial = now?.landed ? now.id : previousId ?? others[0]?.id ?? now?.id
  const selected =
    rounds.find((r) => r.id === chosen) ?? rounds.find((r) => r.id === initial)
  if (!selected) return null

  const label = (r: Round) =>
    [
      r.session_number && `${r.session_number} 回目`,
      formatYmd(r.recorded_on),
      r.id === now?.id ? '今回' : r.id === previousId ? '前回' : '',
    ]
      .filter(Boolean)
      .join(' · ')

  // 回の行。選んだ回の 出す と 記録を削除 は右端 (名前の行の手は今回の手なので、そこに混ぜない。ADR-0086 決定 7)
  const header = (report: NutritionReport | undefined) => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        px: 1.5,
        pt: 1.25,
        pb: selected.id === now?.id && !now.landed ? 1.25 : 0,
      }}
    >
      <PillSelect
        value={selected.id}
        options={rounds.map((r) => ({ value: r.id, label: label(r) }))}
        onChange={setChosen}
        ariaLabel="回"
        menuAlign="left"
      />
      <Box sx={{ flex: 1 }} />
      {report?.generated_at && customer && (
        <NutritionExportButton
          eventId={selected.id}
          report={report}
          customer={customer}
        />
      )}
      {!busy && (
        <NutritionRoundMenu
          eventId={selected.id}
          onDeleted={() => {
            setChosen(null)
            onDeleted?.(selected.id)
          }}
        />
      )}
    </Box>
  )

  if (current && selected.id === now?.id) {
    // 着地する前の今回は回の行だけ (中身は上の段の録音・資料・メモ)
    if (!current.landed)
      return <RoundFrame>{header(current.report)}</RoundFrame>
    return (
      <NutritionRoundTabs
        key={`nutritionRound-${current.event.id}`}
        eventId={current.event.id}
        report={current.report}
        segments={current.event.dictated?.conversation_segments ?? []}
        memo={
          <MemoPanel
            yakurekiId={current.event.id}
            memoItems={current.event.memo_items ?? []}
            flushRef={current.memoFlushRef}
          />
        }
        header={header(current.report)}
        tab={tab}
        onTabChange={setTab}
      />
    )
  }
  return (
    <OtherRound
      key={`nutritionRound-${selected.id}`}
      eventId={selected.id}
      header={header}
      tab={tab}
      onTabChange={setTab}
    />
  )
}

const RoundFrame = ({ children }: { children: ReactNode }) => (
  <Paper
    variant="outlined"
    sx={{ borderRadius: `${RADIUS.xl}px`, overflow: 'hidden' }}
  >
    {children}
  </Paper>
)

// 今回でない回。報告書と、会話の全文・メモのためにその記録を読む。直した欄・栄養価・資料・メモは、それぞれの保存が
// 手元を差し替える (報告書は同じ query、メモは共有の詳細の query を取り直す)。
const OtherRound = ({
  eventId,
  header,
  tab,
  onTabChange,
}: {
  eventId: string
  header: (report: NutritionReport | undefined) => ReactNode
  tab: RoundTab
  onTabChange: (tab: RoundTab) => void
}) => {
  const report = useQuery({
    queryKey: nutritionReportKey(eventId),
    queryFn: () => getNutritionReport(eventId),
    staleTime: Infinity,
  })
  const detail = useQuery({
    queryKey: queryKeys.yakureki.detail(eventId),
    queryFn: async () => (await getYakurekiDetail(eventId)).data,
    staleTime: Infinity,
  })

  if (report.data) {
    return (
      <NutritionRoundTabs
        eventId={eventId}
        report={report.data}
        segments={detail.data?.dictated?.conversation_segments ?? []}
        memo={
          detail.data && (
            <MemoPanel
              yakurekiId={eventId}
              memoItems={detail.data.memo_items ?? []}
            />
          )
        }
        header={header(report.data)}
        tab={tab}
        onTabChange={onTabChange}
      />
    )
  }
  return (
    <RoundFrame>
      {header(undefined)}
      <Box sx={{ p: 2 }}>
        {report.isError ? (
          // 読めなかった (通信の失敗)。回り続けるスピナーにしない
          <BodyText sx={{ color: colorTokens.fg.muted }}>
            この回の報告書を読めませんでした。
            <Link
              component="button"
              variant="body2"
              onClick={() => void report.refetch()}
              sx={{ ml: 1 }}
            >
              もう一度
            </Link>
          </BodyText>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={20} />
          </Box>
        )}
      </Box>
    </RoundFrame>
  )
}
